import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getUsers } from '../mock/users';

const EditClientForm = ({ client, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: client.name || '',
    email: client.email || '',
    specialty: client.specialty || '',
    cell: client.cell || '',
    country: client.country || '',
    googleAdsLink: client.googleAdsLink || '',
    googleMapsLink: client.googleMapsLink || '',
    facebookLink: client.facebookLink || '',
    metaAccountLink: client.metaAccountLink || '',
    salesExecutives: Array.isArray(client.salesExecutives) ? client.salesExecutives : 
                     (client.salesExecutive ? [client.salesExecutive] : [])
  });

  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [availableExecutives, setAvailableExecutives] = useState([]);

  // Cargar usuarios inicialmente
  useEffect(() => {
    const loadUsers = async () => {
      try {
        console.log('EditClientForm - Cargando usuarios...');
        const allUsers = await getUsers();
        console.log('EditClientForm - Usuarios cargados:', allUsers);
        setUsers(allUsers);
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
        setUsers([]);
      }
    };

    loadUsers();
  }, []); // Solo al montar el componente

  // Mostrar TODOS los usuarios disponibles (sin filtrar por rol ni célula)
  useEffect(() => {
    if (users.length === 0) return;

    console.log('EditClientForm - Cargando TODOS los usuarios disponibles');
    console.log('EditClientForm - Usuarios disponibles:', users);
    console.log('EditClientForm - Roles de usuarios:', users.map(u => ({ name: `${u.firstName} ${u.lastName}`, role: u.role })));
    
    // Mostrar TODOS los usuarios disponibles, excluyendo solo administradores si es necesario
    // Pero para máxima flexibilidad, mostrar todos incluso administradores
    const allUsers = users.filter(user => 
      user.firstName && user.lastName // Solo asegurar que tengan nombre
    );
    console.log('EditClientForm - Todos los usuarios disponibles:', allUsers);
    setAvailableExecutives(allUsers);
  }, [users]); // Solo re-ejecutar cuando cambien los usuarios

  const specialties = [
    'Medicina General',
    'Cardiología',
    'Dermatología',
    'Endocrinología',
    'Gastroenterología',
    'Ginecología y Obstetricia',
    'Neurología',
    'Oncología',
    'Oftalmología',
    'Ortopedia y Traumatología',
    'Otorrinolaringología',
    'Pediatría',
    'Psiquiatría',
    'Psicología',
    'Reumatología',
    'Urología',
    'Neumología',
    'Cirugía General',
    'Cirugía Plástica y Reconstructiva',
    'Odontología',
    'Nutriología',
    'Fisioterapia'
  ];

  const cells = [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4'
  ];

  const countries = [
    'México',
    'Estados Unidos',
    'Canadá',
    'España',
    'Argentina',
    'Colombia',
    'Chile',
    'Perú',
    'Ecuador',
    'Venezuela',
    'Uruguay',
    'Paraguay',
    'Bolivia',
    'Costa Rica',
    'Panamá',
    'Guatemala',
    'Honduras',
    'El Salvador',
    'Nicaragua',
    'República Dominicana',
    'Puerto Rico',
    'Cuba'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleExecutiveToggle = (executiveName) => {
    setFormData(prev => {
      const currentExecutives = prev.salesExecutives || [];
      const isSelected = currentExecutives.includes(executiveName);
      
      if (isSelected) {
        // Remover ejecutivo
        return {
          ...prev,
          salesExecutives: currentExecutives.filter(name => name !== executiveName)
        };
      } else {
        // Agregar ejecutivo
        return {
          ...prev,
          salesExecutives: [...currentExecutives, executiveName]
        };
      }
    });
  };

  const removeExecutive = (executiveName) => {
    setFormData(prev => ({
      ...prev,
      salesExecutives: prev.salesExecutives.filter(name => name !== executiveName)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    // Email es opcional, solo validar formato si se proporciona
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!formData.specialty) {
      newErrors.specialty = 'La especialidad es obligatoria';
    }

    if (!formData.cell) {
      newErrors.cell = 'La célula es obligatoria';
    }

    if (!formData.country) {
      newErrors.country = 'El país es obligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const updatedClient = {
        ...client,
        ...formData
      };
      onSave(updatedClient);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto relative z-[10000]">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[#0061A8]">Editar Cliente</h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Información Básica */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8] ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Dr. Juan Pérez"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Opcional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8] ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="doctor@clinica.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Especialidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Especialidad *
                </label>
                <select
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleChange}
                  className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8] ${
                    errors.specialty ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Seleccionar especialidad</option>
                  {specialties.map(specialty => (
                    <option key={specialty} value={specialty}>{specialty}</option>
                  ))}
                </select>
                {errors.specialty && <p className="text-red-500 text-xs mt-1">{errors.specialty}</p>}
              </div>

              {/* Célula */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Célula *
                </label>
                <select
                  name="cell"
                  value={formData.cell}
                  onChange={handleChange}
                  className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8] ${
                    errors.cell ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Seleccionar célula</option>
                  {cells.map(cell => (
                    <option key={cell} value={cell}>{cell}</option>
                  ))}
                </select>
                {errors.cell && <p className="text-red-500 text-xs mt-1">{errors.cell}</p>}
              </div>

              {/* País */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  País *
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8] ${
                    errors.country ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Seleccionar país</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
              </div>

              {/* Ejecutivos de Ventas */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ejecutivos de Ventas
                  <span className="text-xs text-gray-500 ml-2">
                    (Todos los ejecutivos disponibles)
                  </span>
                </label>
                
                {/* Ejecutivos seleccionados */}
                {formData.salesExecutives && formData.salesExecutives.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                      {formData.salesExecutives.map(executive => (
                        <span
                          key={executive}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#0061A8] text-white"
                        >
                          {executive}
                          <button
                            type="button"
                            onClick={() => removeExecutive(executive)}
                            className="ml-2 text-white hover:text-gray-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lista de ejecutivos disponibles */}
                {availableExecutives.length > 0 ? (
                  <div className="border border-gray-300 rounded-md max-h-32 overflow-y-auto">
                    {availableExecutives.map(executive => {
                      const fullName = `${executive.firstName} ${executive.lastName}`;
                      const isSelected = formData.salesExecutives && formData.salesExecutives.includes(fullName);
                      
                      return (
                        <div
                          key={executive.id}
                          className={`p-2 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => handleExecutiveToggle(fullName)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{fullName}</div>
                              <div className="text-xs text-gray-500">
                                Células: {executive.cells ? executive.cells.join(', ') : 'N/A'}
                              </div>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleExecutiveToggle(fullName)}
                                className="h-4 w-4 text-[#0061A8] focus:ring-[#0061A8] border-gray-300 rounded"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 p-3 border border-gray-300 rounded-md bg-gray-50">
                    {formData.cell 
                      ? `No hay ejecutivos disponibles para la célula ${formData.cell}`
                      : 'Selecciona una célula para ver los ejecutivos disponibles'
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Enlaces */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Enlaces (Opcionales)</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Google Ads */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Ads
                  </label>
                  <input
                    type="url"
                    name="googleAdsLink"
                    value={formData.googleAdsLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                    placeholder="https://ads.google.com/..."
                  />
                </div>

                {/* Google Maps */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Maps
                  </label>
                  <input
                    type="url"
                    name="googleMapsLink"
                    value={formData.googleMapsLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                    placeholder="https://maps.app.goo.gl/..."
                  />
                </div>

                {/* Facebook */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Facebook
                  </label>
                  <input
                    type="url"
                    name="facebookLink"
                    value={formData.facebookLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                    placeholder="https://facebook.com/..."
                  />
                </div>

                {/* Meta Business */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meta Business
                  </label>
                  <input
                    type="url"
                    name="metaAccountLink"
                    value={formData.metaAccountLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                    placeholder="https://business.facebook.com/..."
                  />
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#0061A8] text-white rounded-md hover:bg-opacity-90 transition-colors"
              >
                Guardar Cambios
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EditClientForm;
